import styled from '@emotion/styled';
import CheckContained from 'aquilla/icons/misc/CheckContained';
import AquillaButton from 'aquilla/core/molecules/Button';
import Image from '@base/Image';
import Typography from 'aquilla/core/atoms/Typography';
import { mediaQuery, Spacings } from '@styles/index';
import { useIsMobile } from '@stores/AppCommonData';
import { commas } from '@utils/textHelper';
import { useRouter } from 'next/router';
import pushRoute from '@utils/pushRoute';
import { segment } from '@actions/analytics';
import { CHECKOUT_STARTED } from '@constants/segment';
import useGoalEventProps from '@hooks/useGoalEventProps';

const BannerWrapper = styled.div<{ $lastChild: boolean }>`
  width: 100%;
  height: 316px;
  border-top: 1px solid var(--color-i-divider);
  border-bottom: ${({ $lastChild }) =>
    !$lastChild ? '1px solid var(--color-i-divider);' : 'none;'};
  ${mediaQuery.uptoMobile} {
    width: 100%;
    height: 490px;
  }
`;
const InternalBannerWrapper = styled.div`
  display: flex;
  flex-direction: row;
  margin-top: ${Spacings.SPACING_6B};
  justify-content: space-between;
  gap: 10px;
  ${mediaQuery.uptoMobile} {
    flex-direction: column;
    margin-top: ${Spacings.SPACING_4B};
  }
`;

const BannerTextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${Spacings.SPACING_4B};
  ${mediaQuery.uptoMobile} {
    gap: ${Spacings.SPACING_3B};
  }
`;

const AboutWrapper = styled.div`
  display: contents;
  gap: ${Spacings.SPACING_3B};
`;

const BannerTitle = styled(Typography)`
  width: 315px;
  color: var(--color-i-green);
  & > span {
    color: var(--color-i-black);
  }
`;

const BannerSubTextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${Spacings.SPACING_2B};
`;

const BannerSubText = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
  height: ${Spacings.SPACING_5B};
  svg {
    color: var(--color-i-gray-13);
  }
`;

const Text = styled(Typography)`
  color: var(--color-i-black);
`;

const Price = styled(Typography)`
  color: var(--color-i-black);
  & > span {
    font-weight: 700;
  }
`;

const BannerImageWrapper = styled.div`
  width: 366px;
  height: 238px;
  position: relative;
  & > span > img {
    object-fit: cover;
    border-radius: ${Spacings.SPACING_2B};
  }
  ${mediaQuery.uptoMobile} {
    width: 100%;
    max-height: 178px;
  }
`;

const ButtonWrapper = styled.div`
  margin-top: -54px;
  ${mediaQuery.uptoMobile} {
    margin-top: ${Spacings.SPACING_4B};
  }
`;

const SubscriptionButton = styled(AquillaButton)`
  margin-top: ${Spacings.SPACING_4B};
  height: ${Spacings.SPACING_12B};
  ${mediaQuery.uptoMobile} {
    margin-top: ${Spacings.SPACING_3B};
  }
`;

const GetSubscriptionBanner = ({ data }) => {
  const { query } = useRouter();
  const goalUID = query.goalUID as string;
  const goalSegmentData = useGoalEventProps({ goalUID });
  const { lowestPrice, lastChild } = data;
  const isMobile = useIsMobile();

  const subText = [
    'Thousands of live and recorded classes',
    '20,000+ Test & Practice questions',
    'Learn from India’s top educators',
    'Notes, Doubts and many more premium features'
  ];
  const redirectToSubscribe = () => {
    segment.track(CHECKOUT_STARTED, {
      ...goalSegmentData,
      entity_content_type: 'Topology',
      entity_title: 'NA',
      entity_uid: 'NA',
      last_primary_source: 'Store',
      subscription_type: 'PLUS',
      offer_price: 'NA',
      offer_end_date: 'NA',
      subscription_price: 'NA',
      subscription_duration: 'NA'
    });
    const { goalSlug, goalUID } = query;
    pushRoute({
      pathname: '/goal/[goalSlug]/[goalUID]/subscriptions/',
      query: {
        goalSlug,
        goalUID
      }
    });
  };

  const img = 'getPlusSubscriptionBanner.svg';
  return (
    <BannerWrapper $lastChild={lastChild}>
      <InternalBannerWrapper>
        <BannerTextWrapper>
          <AboutWrapper>
            <BannerTitle variant="h4">
              Unlimited access <span>to the best batches & educators</span>
            </BannerTitle>
            <BannerSubTextWrapper>
              {subText.map((item) => {
                return (
                  <BannerSubText key={item}>
                    <CheckContained height={20} width={20} />
                    <Text variant="p3">{item}</Text>
                  </BannerSubText>
                );
              })}
            </BannerSubTextWrapper>
          </AboutWrapper>
        </BannerTextWrapper>
        <BannerImageWrapper>
          <Image src={img} layout="fill" alt="Subscription Banner" />
        </BannerImageWrapper>
      </InternalBannerWrapper>
      <ButtonWrapper>
        {lowestPrice && (
          <Price variant="p2">
            Starts from <span>₹{commas(Math.floor(lowestPrice))}/month</span>
          </Price>
        )}
        <SubscriptionButton
          label="Get Plus subscription"
          onClick={redirectToSubscribe}
          fullWidth={!!isMobile}
          size="medium"
          variant="brand"
        />
      </ButtonWrapper>
    </BannerWrapper>
  );
};

export default GetSubscriptionBanner;
